import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Timer as TimerIcon, Play, Pause, RotateCcw,
  Layers, ListChecks, BarChart3, Music, VolumeX, Sparkles, Loader2, GraduationCap,
  Sun, LifeBuoy, Users,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { awardXp } from "@/lib/gamification.functions";
import { suggestNextStudy } from "@/lib/coach.functions";
import { AssistantMessage } from "@/components/AssistantMessage";
import { toast } from "sonner";
import { usePersistentTimer, requestTimerNotificationPermission, type TimerMode } from "@/hooks/use-persistent-timer";

export const Route = createFileRoute("/_app/studio")({ component: StudioPage });

type Mode = "pomodoro" | "flowtime" | "countdown" | "stopwatch" | "libera";

function useModes(): { id: Mode; label: string; desc: string }[] {
  const { t } = useTranslation();
  return [
    { id: "pomodoro", label: t("studio.modes.pomodoro.label"), desc: t("studio.modes.pomodoro.desc") },
    { id: "flowtime", label: t("studio.modes.flowtime.label"), desc: t("studio.modes.flowtime.desc") },
    { id: "countdown", label: t("studio.modes.countdown.label"), desc: t("studio.modes.countdown.desc") },
    { id: "stopwatch", label: t("studio.modes.stopwatch.label"), desc: t("studio.modes.stopwatch.desc") },
    { id: "libera", label: t("studio.modes.libera.label"), desc: t("studio.modes.libera.desc") },
  ];
}

function StudioPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 px-5 pt-8">
      <PageHeader
        tone="sky"
        icon={<TimerIcon className="h-5 w-5" />}
        title={t("studio.pageTitle")}
        subtitle={t("studio.pageSubtitle")}
      />

      <Link to="/programs" className="tone-sky block">
        <div className="tile flex items-center gap-3 p-4">
          <span className="tile-icon grid h-10 w-10 shrink-0 place-items-center">
            <GraduationCap className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{t("programs.title")}</div>
            <p className="truncate text-xs text-muted-foreground">{t("programs.subtitle")}</p>
          </div>
        </div>
      </Link>

      <Link to="/community" className="tone-lilac block">
        <div className="tile flex items-center gap-3 p-4">
          <span className="tile-icon grid h-10 w-10 shrink-0 place-items-center">
            <Users className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{t("community.title")}</div>
            <p className="truncate text-xs text-muted-foreground">{t("community.subtitle")}</p>
          </div>
        </div>
      </Link>

      <TimerCard />

      <BreaksSection />

      <section className="tone-sage section-block space-y-2">
        <h2 className="section-title px-1">{t("home.sections.review")}</h2>
        <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-2">
          <ShortcutCard to="/flashcards" icon={<Layers className="h-5 w-5" />} label={t("studio.shortcuts.flashcards")} />
          <ShortcutCard to="/quiz" icon={<ListChecks className="h-5 w-5" />} label={t("studio.shortcuts.quiz")} />
          <ShortcutCard to="/stats" icon={<BarChart3 className="h-5 w-5" />} label={t("studio.shortcuts.stats")} />
        </div>
      </section>

      <WhereToStartCard />
      <SpotifyStudyCard />
    </div>
  );
}

/* ------------------------- DA DOVE INIZIO (AI) ---------------------------- */

function BreaksSection() {
  const { t } = useTranslation();
  const cards = [
    { to: "/breaks", icon: <Sun className="h-5 w-5" />, label: t("breaks.hub.summer") },
    { to: "/breaks", icon: <LifeBuoy className="h-5 w-5" />, label: t("breaks.hub.recovery") },
    { to: "/breaks", icon: <GraduationCap className="h-5 w-5" />, label: t("breaks.hub.exam") },
  ];
  return (
    <section className="tone-peach section-block space-y-2">
      <div className="px-1">
        <h2 className="section-title">{t("breaks.title")}</h2>
        <p className="text-xs text-muted-foreground">{t("breaks.subtitle")}</p>
      </div>
      <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-2">
        {cards.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            className="tile flex min-w-0 flex-col items-center gap-1 p-3 text-center transition hover:-translate-y-0.5"
          >
            <div className="tile-icon p-2">{c.icon}</div>
            <span className="w-full truncate text-xs font-medium leading-tight">{c.label}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function WhereToStartCard() {
  const { t } = useTranslation();
  const run = useServerFn(suggestNextStudy);
  const [text, setText] = useState<string>("");
  const [loading, setLoading] = useState(false);

  async function ask() {
    setLoading(true);
    setText("");
    try {
      const res = await run();
      setText(res.text);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("studio.error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="card-soft space-y-3 rounded-3xl border-0 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4 text-primary" />
            {t("studio.whereToStart.title")}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("studio.whereToStart.desc")}
          </p>
        </div>
        <Button size="sm" onClick={ask} disabled={loading} className="shrink-0">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("studio.whereToStart.cta")}
        </Button>
      </div>
      {text && (
        <AssistantMessage
          content={text}
          className="rounded-2xl bg-muted/50 p-3 text-sm leading-relaxed"
        />
      )}
    </Card>
  );
}

/* --------------------------- SPOTIFY STUDY -------------------------------- */

type SpotifyPreset = { id: string; labelKey: string; uri: string };
const SPOTIFY_PRESETS: SpotifyPreset[] = [
  { id: "alpha",    labelKey: "studio.spotify.presets.alpha",    uri: "playlist/37i9dQZF1DWZeKCadgRdKQ" }, // Deep Focus
  { id: "lofi",     labelKey: "studio.spotify.presets.lofi",     uri: "playlist/0vvXsWCC9xrXsKd4FyS8kM" }, // lofi beats
  { id: "classica", labelKey: "studio.spotify.presets.classica", uri: "playlist/37i9dQZF1DWWEJlAGA9gs0" }, // Classical Focus
  { id: "ambient",  labelKey: "studio.spotify.presets.ambient",  uri: "playlist/37i9dQZF1DX3Ogo9pFvBkY" }, // Ambient Relaxation
  { id: "piano",    labelKey: "studio.spotify.presets.piano",    uri: "playlist/37i9dQZF1DX4sWSpwq3LiO" }, // Peaceful Piano
  { id: "nature",   labelKey: "studio.spotify.presets.nature",   uri: "playlist/37i9dQZF1DWVFJtzvDHN4L" }, // Peaceful meditation
];

function SpotifyStudyCard() {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<SpotifyPreset>(SPOTIFY_PRESETS[0]);
  const src = `https://open.spotify.com/embed/${preset.uri}?utm_source=lumen&theme=0`;
  const presetLabel = t(preset.labelKey);

  return (
    <Card className="card-soft space-y-3 rounded-3xl border-0 p-5">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Music className="h-4 w-4 text-primary" />
        {t("studio.spotify.title")}
        <span className="ml-auto text-xs font-normal text-muted-foreground">Spotify</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {SPOTIFY_PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPreset(p)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              preset.id === p.id
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-foreground/70 hover:bg-muted/70"
            }`}
          >
            {t(p.labelKey)}
          </button>
        ))}
      </div>
      <iframe
        key={preset.id}
        title={`Spotify · ${presetLabel}`}
        src={src}
        width="100%"
        height="152"
        frameBorder={0}
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        className="rounded-2xl"
      />
      <p className="text-xs text-muted-foreground">
        {t("studio.spotify.note")}
      </p>
    </Card>
  );
}

function ShortcutCard({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link to={to} className="tile flex min-w-0 flex-col items-center gap-1 p-3 text-center transition hover:-translate-y-0.5">
      <div className="tile-icon p-2">{icon}</div>
      <span className="w-full truncate text-xs font-medium">{label}</span>
    </Link>
  );
}

/* --------------------------------- TIMER ---------------------------------- */

function TimerCard() {
  const { t } = useTranslation();
  const modes = useModes();
  const runAward = useServerFn(awardXp);
  const qc = useQueryClient();

  const award = useMutation({
    mutationFn: async (min: number) => {
      if (min < 5) return;
      return runAward({ data: { reason: "study_session", minutes: min } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-flags"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  async function persistSession(min: number, method: string) {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    await supabase.from("study_sessions").insert({
      user_id: u.user.id,
      duration_minutes: min,
      started_at: new Date(Date.now() - min * 60_000).toISOString(),
      ended_at: new Date().toISOString(),
      method,
    });
  }

  function handleEnd(min: number, m: TimerMode) {
    if (min >= 1) {
      toast.success(t("studio.session.toastEnd", { min }));
      award.mutate(min);
      void persistSession(min, m);
    }
  }

  const timer = usePersistentTimer(handleEnd);
  const { mode, preset, running, displaySec, elapsedSec, setMode, setPreset, toggle, reset } = timer;

  useEffect(() => { requestTimerNotificationPermission(); }, []);

  // Session started from a Programs study plan: preload duration + goal.
  const [intent, setIntent] = useState<{ topic: string; goal: string; minutes: number; method: string } | null>(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("lumen.session-intent");
      if (!raw) return;
      const parsed = JSON.parse(raw) as { topic: string; goal: string; minutes: number; method: string };
      localStorage.removeItem("lumen.session-intent");
      setIntent(parsed);
      setMode("countdown");
      setPreset(Math.max(5, Math.min(120, parsed.minutes || 25)));
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mm = String(Math.floor(displaySec / 60)).padStart(2, "0");
  const ss = String(displaySec % 60).padStart(2, "0");

  const showRing = mode === "pomodoro" || mode === "countdown";
  const pct = showRing ? Math.min(1, Math.max(0, 1 - displaySec / Math.max(1, preset * 60))) : 0;

  return (
    <Card className="card-soft border-0 p-6 text-center">
      {intent && (
        <div className="mb-3 rounded-2xl bg-primary/10 p-3 text-left text-xs">
          <div className="font-semibold">{intent.topic}</div>
          <div className="text-muted-foreground">{intent.goal} · {intent.method} · {intent.minutes} min</div>
        </div>
      )}
      <div className="mb-2 flex items-center justify-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <TimerIcon className="h-3.5 w-3.5" /> {modes.find((m) => m.id === mode)?.label}
        {running && <span className="ml-1 inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-primary" aria-hidden />}
      </div>

      <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
        <SelectTrigger className="mx-auto mb-3 h-8 w-40 rounded-full text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {modes.map((m) => (
            <SelectItem key={m.id} value={m.id}>
              <span className="font-medium">{m.label}</span>
              <span className="ml-2 text-xs text-muted-foreground">{m.desc}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="relative mx-auto my-3 flex h-40 w-40 items-center justify-center">
        {showRing && (
          <svg className="absolute inset-0" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="46" stroke="var(--color-muted)" strokeWidth="4" fill="none" />
            <circle cx="50" cy="50" r="46" stroke="var(--color-primary)" strokeWidth="4" fill="none"
              strokeDasharray={`${pct * 289} 289`} strokeLinecap="round" transform="rotate(-90 50 50)" />
          </svg>
        )}
        <div className="text-4xl font-semibold tabular-nums tracking-tight">{mm}:{ss}</div>
      </div>

      {(mode === "pomodoro" || mode === "countdown") && (
        <div className="mb-3 flex justify-center gap-2">
          {[15, 25, 45, 60].map((m) => (
            <button key={m} onClick={() => setPreset(m)}
              className={`rounded-full border px-3 py-1 text-xs ${preset === m ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
              {m}′
            </button>
          ))}
        </div>
      )}

      <div className="mb-4 flex justify-center gap-2">
        <Button onClick={toggle} className="rounded-xl">
          {running ? <><Pause className="mr-1 h-4 w-4" /> {t("studio.session.pause")}</> : <><Play className="mr-1 h-4 w-4" /> {t("studio.session.start")}</>}
        </Button>
        <Button variant="ghost" aria-label={t("studio.session.resetAria")} onClick={() => {
          const min = reset();
          if (min >= 1) { toast.success(t("studio.session.toastSaved", { min })); award.mutate(min); void persistSession(min, mode); }
        }}>
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>

      {running && (
        <p className="mb-2 text-xs text-muted-foreground">
          {t("studio.session.runningNote")}
        </p>
      )}
      {!running && elapsedSec > 0 && (
        <p className="mb-2 text-xs text-muted-foreground">
          {t("studio.session.pausedNote", { min: Math.floor(elapsedSec / 60), sec: elapsedSec % 60 })}
        </p>
      )}

      <AmbientPlayer />
    </Card>
  );
}

/* --------------------------- AMBIENT SOUND ENGINE -------------------------- */

type Sound = "off" | "white" | "brown" | "pink" | "rain" | "lofi" | "classica";

function useSoundLabels(): Record<Sound, string> {
  const { t } = useTranslation();
  return {
    off: t("studio.sounds.off"),
    white: t("studio.sounds.white"),
    brown: t("studio.sounds.brown"),
    pink: t("studio.sounds.pink"),
    rain: t("studio.sounds.rain"),
    lofi: t("studio.sounds.lofi"),
    classica: t("studio.sounds.classica"),
  };
}

// URL loop-abili royalty free (fallback silenzioso in caso di offline).
const EXTERNAL: Partial<Record<Sound, string>> = {
  lofi: "https://cdn.pixabay.com/download/audio/2022/10/25/audio_0f68a86b4a.mp3",
  classica: "https://cdn.pixabay.com/download/audio/2022/03/15/audio_1808fbf07a.mp3",
};

function AmbientPlayer() {
  const { t } = useTranslation();
  const soundLabels = useSoundLabels();
  const [sound, setSound] = useState<Sound>("off");
  const [volume, setVolume] = useState(0.4);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const nodeRef = useRef<{ node: AudioBufferSourceNode | AudioNode; gain: GainNode } | null>(null);

  useEffect(() => () => stopAll(), []);

  function stopAll() {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (nodeRef.current) {
      try { (nodeRef.current.node as AudioBufferSourceNode).stop?.(); } catch { /* ignore */ }
      try { nodeRef.current.gain.disconnect(); } catch { /* ignore */ }
      nodeRef.current = null;
    }
  }

  useEffect(() => {
    stopAll();
    if (sound === "off") return;

    if (EXTERNAL[sound]) {
      const a = new Audio(EXTERNAL[sound]!);
      a.loop = true; a.volume = volume; a.crossOrigin = "anonymous";
      audioRef.current = a;
      a.play().catch(() => toast.error(t("studio.sounds.playError")));
      return;
    }

    // Synth noise via Web Audio
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = ctxRef.current ?? new AC();
    ctxRef.current = ctx;
    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    if (sound === "white") {
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    } else if (sound === "brown") {
      let last = 0;
      for (let i = 0; i < bufferSize; i++) {
        const w = Math.random() * 2 - 1;
        last = (last + 0.02 * w) / 1.02;
        data[i] = last * 3.5;
      }
    } else if (sound === "pink") {
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < bufferSize; i++) {
        const w = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + w * 0.0555179;
        b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.969 * b2 + w * 0.153852;
        b3 = 0.8665 * b3 + w * 0.3104856;
        b4 = 0.55 * b4 + w * 0.5329522;
        b5 = -0.7616 * b5 - w * 0.016898;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
        b6 = w * 0.115926;
      }
    } else if (sound === "rain") {
      // filtered white noise
      for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.9;
    }

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;

    const gain = ctx.createGain();
    gain.gain.value = volume;

    if (sound === "rain") {
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass"; lp.frequency.value = 1200;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass"; hp.frequency.value = 300;
      src.connect(hp); hp.connect(lp); lp.connect(gain);
    } else {
      src.connect(gain);
    }
    gain.connect(ctx.destination);
    src.start();
    nodeRef.current = { node: src, gain };

    return () => { try { src.stop(); } catch { /* ignore */ } };
  }, [sound]);

  // Live volume updates
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    if (nodeRef.current) nodeRef.current.gain.gain.value = volume;
  }, [volume]);

  return (
    <div className="mt-2 space-y-2 rounded-xl bg-muted/40 p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {sound === "off" ? <VolumeX className="h-3.5 w-3.5" /> : <Music className="h-3.5 w-3.5" />}
        {t("studio.sounds.title")}
      </div>
      <Select value={sound} onValueChange={(v) => setSound(v as Sound)}>
        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {(Object.keys(soundLabels) as Sound[]).map((s) => (
            <SelectItem key={s} value={s}>{soundLabels[s]}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Slider value={[volume]} onValueChange={(v) => setVolume(v[0])} min={0} max={1} step={0.05} disabled={sound === "off"} />
    </div>
  );
}
